import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Live progress for Continuous Monitoring runs.
 *
 * The screen shows the same list of rules in two states:
 *
 *   Nothing running — the list is historical. The database is read once and the
 *                     screen sits still. No repeated asking.
 *   A run going on  — the same list is being rewritten rule by rule. The server
 *                     announces each one as it finishes and the screen redraws
 *                     that row.
 *
 * The socket is an accelerator, never an authority. Every figure it carries can
 * also be read from the database, so a message that never arrives — the socket
 * was down, the tab was closed, Redis dropped it — costs a few seconds of
 * staleness and nothing else. Whenever a run ends or the connection returns, the
 * caller re-reads the database and that reading wins.
 *
 * The connection is held open even when nothing is running. It costs nothing
 * while idle and it removes the case where a run starts and the screen, having
 * decided earlier that nothing was happening, never finds out.
 */

export interface RuleFinishedEvent {
  type: 'rule_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_id: number;
  rule_name: string;
  rows_in: number | null;
  rows_matched: number;
  rows_new: number | null;
  rows_dropped: number | null;
  run_id: number;
  seconds: number;
}

export interface RunStartedEvent {
  type: 'run_started';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_count: number;
  rules: Array<{ rule_id: number; rule_name: string }>;
}

/**
 * A rule that could not be run. The others carry on — they are independent — so
 * this is reported per rule rather than ending the whole run. The screen must
 * show it, otherwise that rule keeps displaying what it found last time as
 * though it had just been confirmed.
 */
/**
 * A worker has picked this rule up. Several rules run at once, so this is what
 * tells the screen which ones are genuinely being worked on at this moment.
 */
export interface RuleStartedEvent {
  type: 'rule_started';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_id: number;
  rule_name: string;
}

export interface RuleFailedEvent {
  type: 'rule_failed';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_id: number;
  rule_name: string;
  run_id: number;
  error: string;
}

export interface RunFinishedEvent {
  type: 'run_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  rules_run?: number;
  rules_failed?: number;
  /** 'partial' means some rules ran and some failed. */
  status: 'done' | 'failed' | 'partial';
  skipped?: string;
  error?: string;
}

/**
 * The whole run is beginning. Carries the sources in the order they will be
 * worked through, so the screen can show the sequence rather than marking
 * everything busy at once.
 */
export interface BatchStartedEvent {
  type: 'batch_started';
  entity_id: number;
  job_id: number;
  source_count: number;
  sources: Array<{ source_id: number; source_name: string }>;
}

/** Work has reached this source. Only this one should appear active. */
export interface SourceStartedEvent {
  type: 'source_started';
  entity_id: number;
  job_id: number;
  source_id: number;
  source_name: string;
}

export interface SourceFinishedEvent {
  type: 'source_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  source_name: string;
  status: 'done' | 'failed';
  rules_run?: number;
  rules_failed?: number;
  error?: string;
}

/**
 * One step of the run is over, with how long it took.
 *
 * These are the same measurements the run already keeps for its own record.
 * Sending them to the screen lets a person see what has been done and what it
 * cost, rather than watching a long step with nothing to read.
 */
export interface StepFinishedEvent {
  type: 'step_finished';
  entity_id: number;
  job_id: number;
  source_id: number | null;
  label: string;
  detail: string;
  seconds: number;
}

/**
 * The data being read in for one data set.
 *
 * This is separate from SourceStarted on purpose. A run has two stages — every
 * data set is read in and prepared, and only then is any of it examined — and
 * the two stages light up different columns. Using one message for both made
 * every data set appear busy during preparation and busy again during
 * detection, which is the overlap a person watching complained about.
 */
export interface IngestStartedEvent {
  type: 'ingest_started';
  entity_id: number;
  job_id: number;
  source_id: number;
  source_name: string;
}

export interface IngestFinishedEvent {
  type: 'ingest_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  source_name: string;
  status: 'done' | 'failed';
  error?: string;
}

/**
 * The preparation rules for one data set are about to be applied. Carries the
 * rules in the order they will run, so the screen can list them before any has
 * finished — the same shape as the detection rules, for the same reason.
 */
export interface PrepStartedEvent {
  type: 'prep_started';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_count: number;
  rules: Array<{
    rule_id: number;
    rule_type: 'clean' | 'formula';
    step: number | null;
    operation: string | null;
    output_column: string | null;
  }>;
}

export interface PrepRuleFinishedEvent {
  type: 'prep_rule_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  rule_id: number;
  seconds: number;
}

export interface PrepFinishedEvent {
  type: 'prep_finished';
  entity_id: number;
  job_id: number;
  source_id: number;
  rules_run: number;
}

export interface BatchFinishedEvent {
  type: 'batch_finished';
  entity_id: number;
  job_id: number;
  /** 'failed' also covers a run abandoned from the screen. */
  status: 'done' | 'failed';
  sources_done: number;
  sources_failed: number;
}

export type CmProgressEvent =
  | RuleFinishedEvent | RuleFailedEvent | RuleStartedEvent
  | RunStartedEvent | RunFinishedEvent
  | BatchStartedEvent | SourceStartedEvent | SourceFinishedEvent | BatchFinishedEvent
  | PrepStartedEvent | PrepRuleFinishedEvent | PrepFinishedEvent
  | IngestStartedEvent | IngestFinishedEvent | StepFinishedEvent;

interface Handlers {
  onRuleFinished?: (e: RuleFinishedEvent) => void;
  onRuleStarted?: (e: RuleStartedEvent) => void;
  onRuleFailed?: (e: RuleFailedEvent) => void;
  onRunStarted?: (e: RunStartedEvent) => void;
  onRunFinished?: (e: RunFinishedEvent) => void;
  onStepFinished?: (e: StepFinishedEvent) => void;
  onIngestStarted?: (e: IngestStartedEvent) => void;
  onIngestFinished?: (e: IngestFinishedEvent) => void;
  onPrepStarted?: (e: PrepStartedEvent) => void;
  onPrepRuleFinished?: (e: PrepRuleFinishedEvent) => void;
  onPrepFinished?: (e: PrepFinishedEvent) => void;
  onBatchStarted?: (e: BatchStartedEvent) => void;
  onSourceStarted?: (e: SourceStartedEvent) => void;
  onSourceFinished?: (e: SourceFinishedEvent) => void;
  onBatchFinished?: (e: BatchFinishedEvent) => void;
  /** Called when the connection is (re-)established, so the caller can re-read
   *  the database and pick up anything missed while it was away. */
  onReconnect?: () => void;
}

type ConnectionState = 'connecting' | 'open' | 'closed';

function socketUrl(entityKey: string): string {
  const apiBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:5001';
  const wsBase = apiBase.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
  return `${wsBase}/api/v1/fan/realtime/ws/${encodeURIComponent(entityKey)}`;
}

export function useCmLiveProgress(entityKey: string | null, handlers: Handlers) {
  const [connection, setConnection] = useState<ConnectionState>('closed');
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<number | null>(null);
  const attemptsRef = useRef(0);
  const closedByUsRef = useRef(false);

  // Handlers are kept in a ref so a caller that re-creates its callbacks on
  // every render does not tear the socket down and open it again.
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  // connect and scheduleRetry call each other, so one of them has to be reachable
  // before it is defined. The retry is held in a ref and filled in below, which
  // keeps both definitions honest without a forward reference that would be
  // undefined at the moment it is needed.
  const scheduleRetryRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!entityKey) return;
    closedByUsRef.current = false;

    let ws: WebSocket;
    try {
      ws = new WebSocket(socketUrl(entityKey));
    } catch {
      // Construction itself failed, which is rare. Try again shortly; the
      // screen remains usable in the meantime because the database read
      // already gave it everything.
      scheduleRetryRef.current();
      return;
    }

    wsRef.current = ws;
    setConnection('connecting');

    ws.onopen = () => {
      setConnection('open');
      const wasRetrying = attemptsRef.current > 0;
      attemptsRef.current = 0;
      // Messages sent while nobody was listening are gone — Redis does not hold
      // them. So on every reconnection the caller re-reads the database rather
      // than assuming the list on screen is still complete.
      if (wasRetrying) handlersRef.current.onReconnect?.();
    };

    ws.onmessage = (ev) => {
      let event: CmProgressEvent;
      try {
        event = JSON.parse(ev.data);
      } catch {
        return;                       // not ours, or malformed
      }
      switch (event.type) {
        case 'rule_finished':   handlersRef.current.onRuleFinished?.(event);   break;
        case 'rule_started':    handlersRef.current.onRuleStarted?.(event);    break;
        case 'rule_failed':     handlersRef.current.onRuleFailed?.(event);     break;
        case 'run_started':     handlersRef.current.onRunStarted?.(event);     break;
        case 'run_finished':    handlersRef.current.onRunFinished?.(event);    break;
        case 'step_finished':       handlersRef.current.onStepFinished?.(event);       break;
        case 'ingest_started':      handlersRef.current.onIngestStarted?.(event);      break;
        case 'ingest_finished':     handlersRef.current.onIngestFinished?.(event);     break;
        case 'prep_started':        handlersRef.current.onPrepStarted?.(event);        break;
        case 'prep_rule_finished':  handlersRef.current.onPrepRuleFinished?.(event);   break;
        case 'prep_finished':       handlersRef.current.onPrepFinished?.(event);       break;
        case 'batch_started':   handlersRef.current.onBatchStarted?.(event);   break;
        case 'source_started':  handlersRef.current.onSourceStarted?.(event);  break;
        case 'source_finished': handlersRef.current.onSourceFinished?.(event); break;
        case 'batch_finished':  handlersRef.current.onBatchFinished?.(event);  break;
        default: break;               // an event type this screen does not use
      }
    };

    ws.onerror = () => {
      // Nothing to do here. onclose follows and handles the retry.
    };

    ws.onclose = () => {
      setConnection('closed');
      wsRef.current = null;
      if (!closedByUsRef.current) scheduleRetry();
    };
  }, [entityKey]);           // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleRetry = useCallback(() => {
    if (retryRef.current !== null) return;
    // Back off gradually to a half-minute so a server that is down does not get
    // hammered, while a brief network blip still recovers almost at once.
    const delay = Math.min(1000 * 2 ** attemptsRef.current, 30000);
    attemptsRef.current += 1;
    retryRef.current = window.setTimeout(() => {
      retryRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      closedByUsRef.current = true;
      if (retryRef.current !== null) {
        window.clearTimeout(retryRef.current);
        retryRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* already gone */ }
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connection };
}

export default useCmLiveProgress;
